import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useParams } from "react-router-dom";
import { useIsFetching } from 'react-query';

import { Spinner } from 'react-bootstrap';
import { Button } from '../../../../components/ui/Button';

import { formatPublishedDate, useTitle } from "../../../../config/global";

import Layout from '../../../Layout/Layout';
import LayoutInfo from "./Setup/LayoutInfo";
import ScreenInfo from "./Setup/ScreenInfo";
import EditLayoutPopUp from './Setup/EditLayoutPopUp';
import PreviewLayoutModal from './Setup/Edit/PreviewLayoutModal';

import { useSelector, useDispatch } from 'react-redux';
import { resetState, setIsPublished } from '../../../../redux/Slices/layoutSlice';
import { useFetchLayoutAspectRatio, useFetchLayoutData, useFetchScreensData, usePublishScreensMutation, useResetLayoutMutation } from '../../../../queries/layouts';
import { useCustomToast } from '../../../Contexts/ToastContext';
import { setHideTabs, setLayer, setMediaIdReset } from '../../../../redux/Slices/Global/globalSlice';
import CreateAnimationLayout from '../../AnimatedLayouts/Create/CreateAnimationLayout';
import { Tooltip } from 'primereact/tooltip';
import { SplitButton } from 'primereact/splitbutton';
import { useResetPlaylistMutation } from '../../../../queries';

const LayoutSetup = () => {
    const { t } = useTranslation();
    useTitle("Layout Setup")
    const { id } = useParams();
    const dispatch = useDispatch();
    const location = useLocation();

    const { showToast } = useCustomToast();
    const { openDialog } = useSelector((state) => state.global);
    const isAnimatedLayout = location.pathname.startsWith('/apps/animated_layouts');

    const { selectedLayout, isPublished, screens_setting, screenDetails } = useSelector((state) => state.layout);
    const { isLoading } = useSelector((state) => state.global);

    const { isLoading: layoutLoading, data: layoutData } = useFetchLayoutData(id);
    const { isLoading: screenLoading, refetch, data: screenData } = useFetchScreensData(id);
    const { isLoading: isFetching } = useFetchLayoutAspectRatio(selectedLayout.aspect_ratio_id);
    const publishScreen = usePublishScreensMutation();
    const resetLayout = useResetLayoutMutation()

    const [showPreview, setShowPreview] = useState(false);

    // Track if any campaign content queries are currently fetching
    const isFetchingContent = useIsFetching(['getCampaignLayerContent']);

    const loading = isFetching || layoutLoading || screenLoading || publishScreen.isLoading

    useEffect(() => {
        dispatch(setHideTabs(false))
        dispatch(setLayer("MAIN"))
        return () => {
            dispatch(resetState());
            dispatch(setMediaIdReset([]))

        };
    }, [])

    const disablePublish = isAnimatedLayout ? false : (selectedLayout?.ads_settings?.live.is_ads_enabled
        ? !screens_setting.some(
            (ScreenInfo) => ScreenInfo?.settings?.live?.is_Ad_Screen_Enabled === true)
        : false)

    const subNavbarProps = useMemo(() => {
        return {
            title: layoutLoading ? t('messages.loading') : layoutData?.title || " ",
            // date: layoutLoading ? "" : selectedLayout?.created_at || "",
            date: layoutLoading ? "" : layoutData?.status === "INACTIVE" ? "Updated at " + formatPublishedDate(layoutData?.updated_at) : "Published at " + formatPublishedDate(layoutData?.published_at) || "",

            note: disablePublish ? t('layouts.setup_ad_screen_required_note') : ""

        };
    }, [selectedLayout, layoutLoading, disablePublish, screenDetails]);

    const publish = async () => {
        await publishScreen.mutateAsync(id,
            {
                onSuccess: () => {
                    dispatch(setIsPublished(true))
                    showToast('success', t('layouts.setup_publish_layout'), t('layouts.setup_publish_success'));
                },
            }
        );
    }


    const reset = async () => {
        await resetLayout.mutateAsync({ id })
    }



    const items = [
        {
            label: <span className="text-[0.85rem]  cursor-auto">
                {
                    selectedLayout.never_published ? t('layouts.setup_not_yet_published')
                        :

                        `${t('layouts.setup_last_published')} ${selectedLayout?.published_at ? formatPublishedDate(selectedLayout?.published_at) : t('layouts.setup_unknown_date')}`
                }
            </span>,
            disabled: true

        },

        {
            label: <span className="fs-6">{t('layouts.setup_discard_changes')}</span>,
            icon: 'bx bx-undo me-2  ',
            disabled: isPublished || disablePublish,
            command: reset
        },

    ];


    const subNavbarContent = useMemo(() => {
        return (
            <div className='flex justify-end w-100'>
                {(isLoading || loading) && (

                    <div className="flex  justify-center items-center"><small className='italic mr-3'>{t('layouts.setup_saving')} </small></div>
                )}

                <Button 
                    label={(isLoading || isFetchingContent > 0) ? t('messages.loading') : t('buttons.preview')}
                    icon={(isLoading || isFetchingContent > 0) ? "pi pi-spin pi-spinner" : "bx bx-show"}
                    className={`p-button-sm mr-2 p-button-outlined px-5 transition-all duration-300 ${
                        (isLoading || isFetchingContent > 0)
                            ? '!cursor-wait !bg-indigo-50 !border-indigo-400 !text-indigo-600 opacity-90' 
                            : ''
                    }`}
                    onClick={() => setShowPreview(true)}
                />

                <SplitButton 
                    label={isLoading ? "" : t('buttons.publish')}
                    icon={loading || isLoading ? "pi pi-spin pi-spinner" : "bx bx-rocket"}
                    buttonClassName={`py-2 border-none transition-all duration-300
                        ${isLoading ? "!w-[117px]" : ""}
                        ${(isPublished || disablePublish) 
                            ? "!cursor-not-allowed p-disabled bg-gray-400 border-gray-400 opacity-60" 
                            : (loading 
                                ? "!cursor-progress !bg-amber-300" 
                                : "!bg-gradient-to-r !from-yellow-300 !to-amber-500 hover:!from-yellow-400 hover:!to-amber-600 shadow-md hover:shadow-amber-400/50 !text-white")
                        }
                    `}
                    menuButtonClassName={`py-2 !w-[30px] leading-[26.5px] border-none
                        ${(isPublished || disablePublish) 
                            ? "bg-gray-400 opacity-60" 
                            : "!bg-amber-500 hover:!bg-amber-600 !text-white"
                        }
                    `}
                    onClick={() => publish()}
                    model={items} 
                />


            </div>
        );
    }, [loading, isPublished, disablePublish, isLoading, selectedLayout, isFetchingContent]);


    return (
        <Layout subNavbarProps={subNavbarProps} subNavbarContent={subNavbarContent} padding={false}>
            {
                loading && (!screenData || !layoutData) ?
                    <div className="col-12 d-flex justify-content-center p-4 border-top border-gray">
                        <Spinner as="span" variant="primary" size='lg' animation="border" role="status" className='mx-3 my-5 ' aria-hidden="true" />
                    </div>
                    :
                    <div className="p-0 flex flex-col md:flex-row">
                        <div className="px-2 mt-4 w-full md:w-[70%] md:ml-5 md:mr-5  h-[85vh] ">
                            <ScreenInfo onPublish={publish} />
                        </div>
                        <div className="w-full md:w-[30%]">
                            <div className="bg-white border">
                                <LayoutInfo refetch={refetch} onPublish={publish} />
                            </div>
                        </div>
                    </div>
            }
            {openDialog.EditLayoutPopUp && <EditLayoutPopUp />}
            {openDialog.CreateAnimationLayout && <CreateAnimationLayout />}
            {showPreview && (
                <PreviewLayoutModal 
                    onClose={() => setShowPreview(false)} 
                    layoutName={selectedLayout.title}
                    screens_setting={screens_setting}
                />
            )}

        </Layout>
    );
};

export default LayoutSetup;
